import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import {
  COMPETENCY_PROFICIENCY_OPTIONS,
  CompetencyProficiency,
  EvaluationPeerReview,
  getPeerReview,
  submitPeerReview,
} from '../../services/peoplePerformanceApi';

type RatingDraft = {
  competencyId: string;
  proficiency: CompetencyProficiency | '';
  comment: string;
};

export function PeerReviewFormPage() {
  const { cycleId, peerReviewId } = useParams();
  const navigate = useNavigate();
  const [review, setReview] = useState<EvaluationPeerReview | null>(null);
  const [ratingDrafts, setRatingDrafts] = useState<Record<string, RatingDraft>>({});
  const [overallComment, setOverallComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isSubmitted = Boolean(review?.submittedAtUtc) || review?.status === 'Submitted';
  const canWrite = !isSubmitted;

  useEffect(() => {
    if (!cycleId || !peerReviewId) return;
    void (async () => {
      setLoading(true);
      try {
        const result = await getPeerReview(cycleId, peerReviewId);
        setReview(result);
        const drafts: Record<string, RatingDraft> = {};
        for (const rating of result.ratings) {
          drafts[rating.competencyId] = {
            competencyId: rating.competencyId,
            proficiency: (rating.proficiency as CompetencyProficiency | null) ?? '',
            comment: rating.comment ?? '',
          };
        }
        setRatingDrafts(drafts);
        setOverallComment(result.overallComment ?? '');
      } catch {
        toast.error('Falha ao carregar avaliação 360.');
        setReview(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [cycleId, peerReviewId]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!cycleId || !peerReviewId || !canWrite) return;

    const missing = Object.values(ratingDrafts).some((item) => !item.proficiency);
    if (missing) {
      toast.error('Informe a nota de todas as competências.');
      return;
    }

    setSaving(true);
    try {
      const result = await submitPeerReview(cycleId, peerReviewId, {
        overallComment,
        ratings: Object.values(ratingDrafts).map((item) => ({
          competencyId: item.competencyId,
          proficiency: item.proficiency || null,
          comment: item.comment,
        })),
      });
      setReview(result);
      toast.success('Avaliação 360 enviada.');
      navigate('/performance');
    } catch {
      toast.error('Falha ao enviar avaliação 360.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <PageShell>
        <p className="text-secondary-500">Carregando...</p>
      </PageShell>
    );
  }

  if (!review) {
    return (
      <PageShell>
        <p className="text-secondary-500">Avaliação 360 não encontrada.</p>
        <Link to="/performance" className="erp-btn-secondary mt-4 inline-flex text-sm">
          Voltar ao desempenho
        </Link>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <PageHeader
        title={`Avaliação 360 — ${review.subjectUserName || 'Participante'}`}
        description={`${review.calendarName || 'Calendário'} · Revisor: ${review.reviewerUserName || '—'}`}
        actions={(
          <Link to="/performance" className="erp-btn-secondary text-sm">
            Voltar ao desempenho
          </Link>
        )}
      />

      {isSubmitted ? (
        <p className="mb-4 rounded-xl border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-600">
          Esta avaliação 360 já foi enviada e não pode mais ser editada.
        </p>
      ) : null}

      <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
        {review.ratings.length === 0 ? (
          <p className="rounded-xl border border-secondary-200 bg-white p-4 text-sm text-secondary-500">
            Nenhuma competência vinculada a esta revisão.
          </p>
        ) : (
          <section className="rounded-xl border border-secondary-200 bg-white p-4 space-y-3">
            {review.ratings.map((rating) => {
              const draft = ratingDrafts[rating.competencyId];
              return (
                <div
                  key={rating.competencyId}
                  className="grid gap-3 border-t border-secondary-100 pt-3 first:border-t-0 first:pt-0 md:grid-cols-[1fr_220px]"
                >
                  <div>
                    <p className="font-medium text-secondary-900">
                      {rating.competencyName}
                    </p>
                    <label className="erp-label mt-2 block">
                      Comentário (opcional)
                      <textarea
                        className="erp-input mt-1"
                        rows={2}
                        disabled={!canWrite}
                        value={draft?.comment ?? ''}
                        onChange={(event) =>
                          setRatingDrafts((current) => ({
                            ...current,
                            [rating.competencyId]: {
                              competencyId: rating.competencyId,
                              proficiency: current[rating.competencyId]?.proficiency ?? '',
                              comment: event.target.value,
                            },
                          }))
                        }
                      />
                    </label>
                  </div>
                  <label className="erp-label">
                    Nota
                    <select
                      className="erp-input mt-1"
                      required
                      disabled={!canWrite}
                      value={draft?.proficiency ?? ''}
                      onChange={(event) =>
                        setRatingDrafts((current) => ({
                          ...current,
                          [rating.competencyId]: {
                            competencyId: rating.competencyId,
                            proficiency: event.target.value as CompetencyProficiency | '',
                            comment: current[rating.competencyId]?.comment ?? '',
                          },
                        }))
                      }
                    >
                      <option value="">Selecione...</option>
                      {COMPETENCY_PROFICIENCY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.score} — {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              );
            })}
          </section>
        )}

        <section className="rounded-xl border border-secondary-200 bg-white p-4">
          <label className="erp-label block">
            Comentário geral (opcional)
            <textarea
              className="erp-input mt-1"
              rows={3}
              disabled={!canWrite}
              value={overallComment}
              onChange={(event) => setOverallComment(event.target.value)}
            />
          </label>
        </section>

        {canWrite ? (
          <div className="flex justify-end">
            <button
              type="submit"
              className="erp-btn-primary"
              disabled={saving || review.ratings.length === 0}
            >
              {saving ? 'Enviando...' : 'Enviar avaliação 360'}
            </button>
          </div>
        ) : null}
      </form>
    </PageShell>
  );
}
